import {Fragment} from 'react'
import {Link} from 'react-router'
import {mirroraiOrganization} from '@core/products/organization'
import type {PackIndexItem, SeriesGroup} from '@core/packs/series/types'
import type {HouseHome, HouseIdentity} from '@core/wall/home/doorstep'
import faq from '@product/narcis/components/Faq/faq.json'
import {FeaturedOn} from '@product/narcis/components/FeaturedOn/FeaturedOn'
import packIndexStyles from '@product/narcis/components/PackIndex/PackIndex.module.scss'
import seoBlocks from '@product/narcis/content/seoBlocks.json'
import {ISSUES, issuePath} from '@product/narcis/content/issues'
import {MODEL_PRICE, PRICE_LINE, SITE_DESCRIPTION, withCanon} from '@product/narcis/proposition'
import {tileImageUrl} from '@product/narcis/wall/urls'

import styles from './endpaper.module.scss'
import {surfaceOgImageUrl} from '@core/og/cardUrls'

/**
 * Narcis's home doorstep (narcis wall plan step 6 § 1; the doorstep shape from
 * `2026_09_24_wall_home_doorstep.md`). The endpaper repeats the page's own meta title and
 * description as its H1 and lede; the lede ends with a link to each issue (`/for/<slug>`). The WebApplication offer is the model at its price; the
 * guarantee stays out of structured data.
 */
const ORIGIN = 'https://narcis.ai'

const META = {
    title: 'Narcis — AI Photoshoot: 35,000 Photographs Waiting for Your Face',
    description: SITE_DESCRIPTION,
}

export const narcisHouse: HouseIdentity = {
    origin: ORIGIN,
    meta: META,
    organization: {
        name: 'Narcis',
        logo: `${ORIGIN}/narcis-web-app-manifest-512x512.png`,
        description:
            'Narcis creates an AI model of you from your photos and makes photos of you with it, in ' +
            'curated packs. It also offers a face swap. It runs on its own servers in the EU.',
        parentOrganization: mirroraiOrganization,
    },
    webApplication: {
        name: 'Narcis.ai - AI Photoshoot & Face Swap',
        alternateName: ['Narcis AI', 'AI Photoshoot', 'AI Photo Generator', 'AI Portrait Generator', 'Face Swap Online'],
        description:
            'Create your AI model from your photos, then photos of you in every pack on Narcis. ' +
            'Or swap your face onto any photo.',
        url: ORIGIN,
        applicationCategory: 'MultimediaApplication',
        offers: {
            price: (MODEL_PRICE.amountCents / 100).toFixed(2),
            priceCurrency: MODEL_PRICE.currency,
            description: `Your AI model, created from your photos. ${PRICE_LINE}`,
        },
        featureList: [
            'Your AI model, created from your photos',
            'Every pack on Narcis made with your face, nine photos per pack',
            'Single photos made with your face',
            'Face Swap - Put your face on any photo',
            'No download required',
        ],
        screenshot: surfaceOgImageUrl(ORIGIN, 'home'),
    },
    tileImageUrl,
}

/** The loader's endpaper read — the catalog lives server-side, so its counts and links travel in the
 *  payload rather than shipping the registry to the client. */
export type NarcisEndpaperData = {
    totalPacks: number
    packTeaser: {slug: string; name: string}[]
    packIndexGroups: SeriesGroup<PackIndexItem>[]
}

/** Every working link the pre-wall home offered, for indexers — none mounts its own component on
 *  the wall; only the link survives. */
const WORKING_LINKS = [
    {href: '/packs', label: 'Packs'},
    {href: '/portrait', label: 'Portrait'},
    {href: '/tools/swap', label: 'Face swap'},
    {href: '/tools', label: 'Tools'},
    {href: '/pricing', label: 'Pricing'},
]

export function narcisHome({totalPacks, packTeaser, packIndexGroups}: NarcisEndpaperData): HouseHome {
    const live = {PACK_COUNT: String(totalPacks)}
    return {
        ...narcisHouse,
        styles,
        endpaper: {
            intro: {
                h1: META.title,
                lede: (
                    <>
                        {META.description} Read the issue written for you:{' '}
                        {ISSUES.map((issue, i) => (
                            <Fragment key={issue.slug}>
                                {i === 0 ? '' : i === ISSUES.length - 1 ? ', or ' : ', '}
                                <Link to={issuePath(issue)} className={styles.ledeLink}>{issue.persona}</Link>
                            </Fragment>
                        ))}
                        .
                    </>
                ),
                stats: `${(totalPacks * 9).toLocaleString('en-US')} photographs · nine of you in every pack`,
            },
            catalog: {
                h2: 'Packs',
                headerLinks: WORKING_LINKS,
                links: packTeaser.map((p) => ({label: p.name, href: `/${p.slug}`})),
                seeAll: {label: `See all ${totalPacks} packs →`, href: '/packs'},
            },
            sections: seoBlocks.map((entry) => ({
                h2: entry.heading,
                body: (
                    <>
                        <p className={styles.sectionBody}>{withCanon(entry.body, live)}</p>
                        <div className={styles.sectionExpand}>{withCanon(entry.expand, live)}</div>
                    </>
                ),
            })),
            faq: {entries: faq.map((e) => ({question: e.question, answer: withCanon(e.answer)})), iconSize: 32},
            packIndex: {groups: packIndexGroups, total: totalPacks, styles: packIndexStyles},
            coda: <FeaturedOn />,
        },
    }
}
